import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { LinkButton } from "../../core/basic/Button";
import { useMissingTrellisAdsProfiles } from "./MissingProfilesSections";
import { toAmazonProfiles } from "./useAmazonProfiles";
import { FaTriangleExclamation } from "react-icons/fa6";

const ProfileHealthSection = () => {
  const { t } = useTranslation(["onboarding"]);
  const [showProfileHealthDetails, setShowProfileHealthDetails] = useState(false);
  const { data: missingProfilesData, isLoading: isMissingProfilesDataLoading } = useMissingTrellisAdsProfiles();
  const { profiles_missing_write_access, profiles_with_invalid_payment_methods } = missingProfilesData ?? {};

  const profilesMissingWriteAccess = useMemo(() => {
    return toAmazonProfiles(profiles_missing_write_access);
  }, [profiles_missing_write_access]);

  const profilesWithInvalidPaymentMethods = useMemo(() => {
    return toAmazonProfiles(profiles_with_invalid_payment_methods);
  }, [profiles_with_invalid_payment_methods]);

  const hasProfileHealthIssues = profilesMissingWriteAccess.length > 0 || profilesWithInvalidPaymentMethods.length > 0;

  if (isMissingProfilesDataLoading || !hasProfileHealthIssues) {
    return null;
  }

  return (
    <VStack
      className={css({
        border: "1px solid",
        borderColor: "border.default",
        borderRadius: "medium",
        marginBottom: "large",
        padding: "medium",
        backgroundColor: "gray.50",
      })}
      gap="small"
      alignItems="stretch"
    >
      <Flex gap="small" justifyContent="space-between" alignItems="center" wrap="wrap">
        <VStack gap="xxsmall" alignItems="flex-start" maxWidth={"80%"}>
          <p
            className={css({
              display: "flex",
              gap: "small",
              alignItems: "center",

              fontSize: "large",
              fontWeight: "600",
            })}
          >
            <FaTriangleExclamation /> {t(($) => $.onboarding["Profile Health"])}
          </p>
          <p className={css({ marginBottom: "0", fontSize: "medium" })}>
            {t(
              ($) =>
                $.onboarding[
                  "We found profiles needing attention. Connected profile campaigns may stop serving due to payment issues and Trellis will no longer be able to manage campaigns if edit access is lost. Additionally, profiles cannot be added to Trellis if they are experiencing either of these issues."
                ],
            )}
          </p>
          <p className={css({ marginY: "medium", fontSize: "medium" })}>
            {t(($) => $.onboarding["Issues found:"])}{" "}
            {[
              profilesMissingWriteAccess.length ?
                t(($) => $.onboarding["{count} read-only access"], { count: profilesMissingWriteAccess.length })
              : null,
              profilesWithInvalidPaymentMethods.length ?
                t(($) => $.onboarding["{count} invalid payment method"], {
                  count: profilesWithInvalidPaymentMethods.length,
                })
              : null,
            ]
              .filter(Boolean)
              .join(", ")}
            .
          </p>
        </VStack>
        <LinkButton
          onPress={() => setShowProfileHealthDetails(!showProfileHealthDetails)}
          className={css({ fontSize: "medium" })}
        >
          {showProfileHealthDetails ? t(($) => $.onboarding["Hide Profiles"]) : t(($) => $.onboarding["Show Profiles"])}
        </LinkButton>
      </Flex>
      <p className={css({ marginBottom: "0", fontSize: "small" })}>
        {t(
          ($) =>
            $.onboarding[
              "Fix read-only access by granting View & Edit permissions in Campaign Manager, and update billing to resolve payment method issues."
            ],
        )}
      </p>
      {showProfileHealthDetails && (
        <VStack className={css({ marginTop: "small" })} gap="small" alignItems="stretch">
          {profilesMissingWriteAccess.length > 0 && (
            <VStack gap="xsmall" alignItems="stretch">
              <p className={css({ marginBottom: "0", fontSize: "small", fontWeight: "600" })}>
                {t(($) => $.onboarding["Read-only access"])}
              </p>
              {profilesMissingWriteAccess.map((profile) => (
                <div key={`ro-${profile.profile_id}`} className={css({ fontSize: "small" })}>
                  {profile.profile_name} · {profile.marketplaces.join(", ")}
                </div>
              ))}
            </VStack>
          )}
          {profilesWithInvalidPaymentMethods.length > 0 && (
            <VStack gap="xsmall" alignItems="stretch">
              <p className={css({ marginBottom: "0", fontSize: "small", fontWeight: "600" })}>
                {t(($) => $.onboarding["Invalid payment method"])}
              </p>
              {profilesWithInvalidPaymentMethods.map((profile) => (
                <div key={`pm-${profile.profile_id}`} className={css({ fontSize: "small" })}>
                  {profile.profile_name} · {profile.marketplaces.join(", ")}
                </div>
              ))}
            </VStack>
          )}
        </VStack>
      )}
    </VStack>
  );
};

export default ProfileHealthSection;
